import { Image } from '@lumapps/lumx-images/types';
import { Wrex } from '@lumapps/wrex/types';

/**
 * List of available views
 */
export enum TRAINING_COURSE_VIEWS {
    ACTIVITIES = 'activities',
    TOOLBOXES = 'toolboxes',
    LEADERBOARD = 'leaderboard',
    ACHIEVEMENTS = 'achievements',
}

export enum MODULE_STATUSES {
    TODO = 'todo',
    STARTED = 'started',
    COMPLETED = 'completed',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    LOCKED = 'locked',
}

export enum ACTIVITY_STATUSES {
    TODO = 'todo',
    STARTED = 'started',
    COMPLETED = 'completed',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    LOCKED = 'locked',
}

export enum LEARNING_OBJECT_STATUSES {
    TODO = 'todo',
    STARTED = 'started',
    COMPLETED = 'completed',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    LOCKED = 'locked',
}

/* List all different type of activities */
export enum ACTIVITY_TYPES {
    MATCH = 'match',
    COURSE = 'course',
    EVALUATION = 'evaluation',
    QUIZ = 'quiz',
    SEQUENCE = 'sequence',
}

export enum ACTIVITY_CONCLUSION_TYPES {
    NORMAL = 'normal',
    SCORED = 'scored',
    OUTCOME = 'outcome',
    DUEL = 'duel',
}

/* List all different type of rendering for the 'course' activity */
export enum COURSE_RENDER_TYPE {
    MOBILE_COURSE = 'mobileCourse',
    FLASH_GAME = 'flashGame',
    TOOLBOX = 'toolbox',
}

export interface LearningString {
    value: string;
    lang?: string;
}

export interface LearningRichText {
    value: Wrex.Nodes;
    lang?: string;
}

export interface ActivityIntroduction {
    title: LearningString;
    description: LearningRichText;
    image: Image;
}

export interface ConclusionOutcome {
    text: LearningRichText;
    formScore: number;
    toScore: number;
}

export interface ActivityConclusion {
    type: ACTIVITY_CONCLUSION_TYPES;
    title: LearningString;
    description?: LearningRichText;
    success?: LearningRichText;
    failure?: LearningRichText;
    perfect?: LearningRichText;
    won?: LearningRichText;
    lost?: LearningRichText;
    done?: LearningRichText;
    outcomes?: ConclusionOutcome[];
}

export interface ActivityConfig {
    renderType: string;
    images: Image[];
}

export enum QUIZ_ANSWER_STATE {
    CORRECT_RESULTS,
    INCORRECT_RESULTS,
    DEFAULT,
    NEUTRAL,
    NEUTRAL_SELECTED,
}

export interface Activity {
    id: string;
    title: LearningString;
    type: ACTIVITY_TYPES;
    introduction?: ActivityIntroduction;
    conclusion?: ActivityConclusion;
    config?: ActivityConfig;
    status?: ACTIVITY_STATUSES;
    progress?: number;
    trainingId?: string;
    siteId?: string;
}

export interface Module {
    id: string;
    title: LearningString;
    status: MODULE_STATUSES;
    progress: number;
    cover: Image;
    activities: Activity[];
}

export interface ToolboxSection {
    id: string;
    title: LearningString;
    description: LearningRichText;
    cover: Image;
    activities: Activity[];
}

export interface Toolbox {
    id: string;
    title: LearningString;
    description: LearningRichText;
    cover: Image;
    sections: ToolboxSection[];
}

export interface QuizAnswer {
    id: string;
    answer: LearningRichText;
    isCorrect: boolean;
    scoreValue?: number;
    feedback?: LearningRichText;
}

export interface QuizQuestion {
    id: string;
    question: LearningRichText;
    hasMultipleAnswers?: boolean;
    answers: QuizAnswer[];
    correction?: LearningRichText;
    isOptional?: boolean;
}

export interface QuizActivity extends Activity {
    questions: QuizQuestion[];
}

export enum COURSE_CARD_TYPES {
    INFO = 'info',
    FLIP = 'flip',
    TRANSITION = 'transition',
    QUIZ = 'quiz',
    SURVEY = 'survey',
    SLIDER_QUIZ = 'slider_quiz',
}

export interface CourseCard {
    id: string;
    type: COURSE_CARD_TYPES;
    recto?: LearningRichText;
    verso?: LearningRichText;
    quiz?: QuizQuestion;
    question?: LearningRichText;
    isOptional?: boolean;
    rectoFlipButton?: LearningString;
    versoFlipButton?: LearningString;
}

export interface CourseActivity extends Activity {
    cards: CourseCard[];
}

export const enum TRAINING_TYPE {
    TRAINING = 'training_course',
    LEARNING_PATH = 'learning_path',
    RECOMMENDATION_QUIZ = 'recommendation_quiz',
    SCORM_TRAINING = 'scorm_training',
}

export interface Category {
    id: string;
    title: LearningString;
    cover: Image;
}

export interface Language {
    code: string;
    name: string;
    native: string;
    regional: string;
    isRtl: boolean;
}

export interface Training {
    id: string;
    type: TRAINING_TYPE;
    title: LearningString;
    description?: LearningRichText;
    cover?: Image;
    categories?: Category[];
    languages?: Language[];
    isSandbox?: boolean;
    canComment?: boolean;
    hasAchievements?: boolean;
    hasLeaderboard?: boolean;
    toolboxes?: Toolbox[];
    status?: LEARNING_OBJECT_STATUSES;
    progress?: number;
    score?: number;
    success?: boolean;
    totalTime?: number;
    totalPoints?: number;
    sessionsCount?: number;
    isRecommended?: boolean;
    dueAt?: Date;
    accessEndAt?: Date;
}

export interface TrainingCourse extends Training {
    type: TRAINING_TYPE.TRAINING;
    modules: Module[];
}

export interface LearningPath extends Training {
    type: TRAINING_TYPE.LEARNING_PATH;
    items: Training[];
}

export enum SCORM_VERSIONS {
    SCORM_1_2 = '1.2',
    SCORM_2004 = '2004',
}

export interface ScormTraining extends Training {
    type: TRAINING_TYPE.SCORM_TRAINING;
    resourceHref: LearningString;
    scormVersion: LearningString;
    scormPlayerUrl: string;
    suspendData?: string;
    launchData?: string;
    location?: string;
}

export interface RecommendationQuiz extends Training {
    type: TRAINING_TYPE.RECOMMENDATION_QUIZ;
}

export interface EvaluationAnswerTheme {
    themeId: string;
    scoreValue: number;
}

export interface EvaluationAnswer {
    id: string;
    answer: LearningRichText;
    themes: EvaluationAnswerTheme[];
}

export interface EvaluationQuestion {
    id: string;
    question: LearningRichText;
    answers: EvaluationAnswer[];
    correction?: LearningRichText;
}

export enum EVALUATION_THEME_OUTCOME_TYPES {
    TRAINING = 'training',
    MODULE = 'module',
}

export interface MatchSet {
    id: string;
    name: LearningString;
    image: Image;
}

export interface MatchItem {
    id: string;
    setId: string;
    item: LearningRichText;
    scoreValue: number;
}

export interface MatchActivity extends Activity {
    sets: MatchSet[];
    items: MatchItem[];
}

export enum SEQUENCE_QUESTION_TYPES {
    IMAGE = 'image',
    SENTENCE = 'sentence',
    WORD = 'word',
}

export interface SequenceAnswer {
    id: string;
    answer: LearningRichText;
    isCorrect: boolean;
    scoreValue?: number;
}

export interface SequenceQuestion {
    id: string;
    type: SEQUENCE_QUESTION_TYPES;
    question: LearningRichText;
    correction?: LearningRichText;
    correct: Image[] | string[];
    trap: Image[] | string[];
}

export interface SequenceActivity extends Activity {
    questions: SequenceQuestion[];
}

/**
 * All possible session action types.
 */
export enum SESSION_ACTION_TYPES {
    ACTIVITY_START = 'activity_start',
    ACTIVITY_DIFFICULTY_SELECTED = 'activity_difficulty_selected',
    ACTIVITY_OPPONENT_SELECTED = 'activity_opponent_selected',
    ACTIVITY_QUIZ_QUESTION_ANSWERED = 'activity_quiz_question_answered',
    ACTIVITY_MATCH_QUESTION_ANSWERED = 'activity_match_question_answered',
    ACTIVITY_EVALUATION_QUESTION_ANSWERED = 'activity_evaluation_question_answered',
    ACTIVITY_PAUSE = 'activity_pause',
    ACTIVITY_RESUME = 'activity_resume',
    ACTIVITY_END = 'activity_end',
}

/**
 * Action when an activity starts.
 */
export interface SessionActionActivityStart {
    type: SESSION_ACTION_TYPES.ACTIVITY_START;
    siteId: string;
}

/**
 * Action when a difficulty is selected.
 */
export interface SessionActionActivityDifficultySelected {
    type: SESSION_ACTION_TYPES.ACTIVITY_DIFFICULTY_SELECTED;
    difficultyId: string;
}

/**
 * Action when an opponent is selected.
 */
export interface SessionActionActivityOpponentSelected {
    type: SESSION_ACTION_TYPES.ACTIVITY_OPPONENT_SELECTED;
    opponentId: string;
}

/**
 * Action when a quiz question is answered.
 */
export interface SessionActionActivityQuizQuestionAnswered {
    type: SESSION_ACTION_TYPES.ACTIVITY_QUIZ_QUESTION_ANSWERED;
    questionId: string;
    answersId: string[];
    elapsedTime: number;
}

/**
 * Action when an activity is paused.
 */
export interface SessionActionActivityPause {
    type: SESSION_ACTION_TYPES.ACTIVITY_PAUSE;
}

/**
 * Action when an activity is resumed.
 */
export interface SessionActionActivityResume {
    type: SESSION_ACTION_TYPES.ACTIVITY_RESUME;
}

export interface SessionActionActivityEndLegacyData {
    created?: Date;
    time?: number;
    progress?: number;
    points?: number;
    answers?: {
        question: string;
        right: boolean;
        answer: string;
    }[];
    score?: number;
    success?: boolean;
    perfect?: boolean;
}
/**
 * Action when an activity ends.
 */
export interface SessionActionActivityEnd {
    type: SESSION_ACTION_TYPES.ACTIVITY_END;
    legacyData: {
        siteId: string;
        trainingId: string;
        activityId: string;
        learnerId: string;
        data: SessionActionActivityEndLegacyData;
    };
}

/**
 * All possible session actions.
 */
export type SessionAction =
    | SessionActionActivityStart
    | SessionActionActivityDifficultySelected
    | SessionActionActivityOpponentSelected
    | SessionActionActivityQuizQuestionAnswered
    | SessionActionActivityPause
    | SessionActionActivityResume
    | SessionActionActivityEnd;

/**
 * Session event.
 */
export interface SessionEvent {
    action: SessionAction;
    sessionId: string;
    createdAt: Date;
}

/**
 * Session.
 */
export interface Session {
    sessionId: string;
    siteId: string;
    learnerId?: string;
    trainingId: string;
    activityId?: string;
    activityType?: ACTIVITY_TYPES;
    createdAt: Date;
    events: SessionEvent[];
}

export const enum TRAINING_LEGACY_MESSAGE_TYPE {
    NAVIGATION = 'askNavigation',
    BACK = 'askBack',
    OPEN_URL = 'askOpenUrl',
    OPEN_DOCUMENT = 'askOpenDocument',
    SCORM_PLAYER_READY = 'scorm_player_ready',
    SCORM_PLAYER_TERMINATE = 'scorm_player_terminate',
}

export const enum TRAINING_LEGACY_MESSAGE_NAVIGATION_TYPE {
    EVENT = 'event',
}

export const enum TRAINING_LEGACY_DATA_TYPE {
    TRAINING = 'Training',
    LEARNING_PATH = 'LearningPath',
    RECOMMENDATION_QUIZ = 'RecommendationQuiz',
}

export interface TrainingLegacy {
    accessEndsAt: Date;
    accessUntil: Date;
    availableLanguages: string[];
    categoriesCodes: string[];
    categoriesTitles: string[];
    code: string;
    cover: string;
    description: string;
    dueAt: Date;
    dueDate: Date;
    identifier: string;
    isDemo: boolean;
    isLive: boolean;
    isRecommended: boolean;
    isSandbox: boolean;
    language: string;
    logo: string;
    nbCompletableActivities: number;
    nbCompletedActivities: number;
    progress: number;
    title: string;
    type: TRAINING_TYPE | TRAINING_LEGACY_DATA_TYPE;
}

export interface TrainingLegacyList {
    offset: number;
    limit: number;
    items: TrainingLegacy[];
}

export interface TrainingLegacyLaunchUrl {
    launchUrl: string;
    identifier: string;
    title: string;
    type: string;
}

export interface CategoryLegacy {
    title: string;
    image: string;
    code: string;
    parent?: string;
    children?: CategoryLegacy[];
    trainings?: TrainingLegacy[];
}

export interface CatalogLegacy {
    categories?: CategoryLegacy[];
    trainings?: TrainingLegacy[];
}

export interface CatalogSearch {
    query: string;
    results: TrainingLegacy[];
}

export interface FlatenedCategory {
    categoryData: CategoryLegacy;
    parentCode: string;
    depth: number;
}

export interface EvaluationThemeOutcome {
    id: string;
    type: EVALUATION_THEME_OUTCOME_TYPES;
    text: LearningRichText;
    fromScore: number;
    toScore: number;
    trainings?: TrainingLegacy[];
    modules?: Module[];
}

export interface EvaluationTheme {
    id: string;
    title: LearningRichText;
    maxScore: number;
    outcomes: EvaluationThemeOutcome[];
}

export interface EvaluationActivity extends Activity {
    questions: EvaluationQuestion[];
    themes: EvaluationTheme[];
    globalTheme: EvaluationTheme;
}
